/**
 * Brand casing — the one place that knows how a brand name is written
 * (FRIDGE-348, 08/17/2026: "product pages need to respect brand names. for
 * example i see Lg Studio -- instead of LG Studio. see if there are others.").
 *
 * WHY THIS EXISTS. Brand names reach the product page from three places and
 * two of them mangle casing:
 *
 *   1. `products.brand` / `canonical_products.brand` are stored as the feed
 *      delivered them, and some feeds title-case: live on 08/17/2026 the
 *      active catalog carried "Lg Studio" (3 rows), "Lg Electronics" (4 rows;
 *      43 more in canonical_products), "Aeg", "Erp", "Impecca Usa", "Jeremy
 *      Cass Llc", "Lifeplus", "Makspro", "Peakcold", "Pioneerworks", "Smad",
 *      "Duura", "Earthsmart", "Ecevt", "Mzavp", "Jimmy-Us", "Waterdrop-Uk",
 *      "F2c Global Limited", "Everydrop By Whirlpool". Every PDP surface that
 *      printed the column verbatim ("Similar french doors from Lg Studio", the
 *      Quick Comparison brand line, the verdict headline, the spec FAQ, JSON-LD
 *      brand.name, <meta name="keywords">) repeated the mistake.
 *   2. `titleCaseValue` (lib/spec-display-policy.ts) normalizes ALL-CAPS feed
 *      values for spec rows — right for "WHITE", wrong for "LG": the
 *      "Compatible Brands: LG" row on a water-filter PDP rendered "Lg".
 *
 * WHAT IT DOES. `displayBrandName(raw)` returns the canonical casing when the
 * value is a brand we know, and returns the raw value UNCHANGED otherwise. It
 * never title-cases, upper-cases, or otherwise "improves" an unknown brand —
 * an unknown maker's own casing is more likely right than any rule we could
 * invent, and hOmeLabs / iRV / everydrop prove that no rule exists.
 *
 * WHERE THE CANONICAL FORMS COME FROM. The active `products.brand` list and
 * the `brands` registry table were pulled live on 08/17/2026 and every entry
 * below is either (a) the majority form across those two tables, (b) the
 * registry's form where the two disagree (the registry is what the /brands
 * page shows, so PDP and brand page stay consistent), or (c) the owner's
 * directed sub-brand forms (LG STUDIO → "LG Studio", LG SIGNATURE →
 * "LG Signature", GE Café → "Café", GE Monogram → "Monogram"). Where the data
 * offered no clear majority and no registry row (Icepure/ICEPURE,
 * Ca'lefort/Ca'Lefort, Kucht/KUCHT) the brand is deliberately ABSENT — the raw
 * value passes through untouched rather than being guessed at.
 *
 * Lookups are keyed on a normalized form (lower-case, accents stripped,
 * hyphens/underscores/whitespace collapsed to one space, "and" → "&"), so
 * "LG STUDIO", "Lg Studio", "lg-studio" and "lg studio" all resolve to
 * "LG Studio", and "sub zero" / "Sub-Zero" / "sub-zero" to "Sub-Zero".
 *
 * Kept separate from lib/brand-rules.ts on purpose: BRAND_RULES is a
 * free-text substitution table applied to titles by formatBreadcrumbText;
 * this module is a whole-value lookup for a brand FIELD and must never touch
 * prose.
 */

/**
 * Canonical display forms. One entry per brand, written exactly as it should
 * appear on the page. Alphabetical within each group so a diff reads clean.
 */
const CANONICAL_BRANDS: readonly string[] = [
  // ── The major appliance makers and their sub-brands ─────────────────────
  'AEG',
  'Amana',
  'Beko',
  'Bertazzoni',
  'Blomberg',
  'Bosch',
  'Café',
  'Crosley',
  'Crosley Appliances',
  'Dacor',
  'Electrolux',
  'Fisher & Paykel',
  'Frigidaire',
  'Frigidaire Gallery',
  'Frigidaire Professional',
  'Frigidaire USA',
  'Fulgor Milano',
  'Galanz',
  'GE',
  'GE Appliances',
  'GE Profile',
  'Gorenje',
  'Haier',
  'Hisense',
  'Hotpoint',
  'Insignia',
  'JennAir',
  'Kenmore',
  'KitchenAid',
  'LG',
  'LG Electronics',
  'LG Signature',
  'LG Studio',
  'Liebherr',
  'Maytag',
  'Midea',
  'Miele',
  'Monogram',
  'Panasonic',
  'RCA',
  'Samsung',
  'Sharp',
  'Smeg',
  'Sub-Zero',
  'TCL',
  'Thermador',
  'Toshiba',
  'Viking',
  'Whirlpool',
  'Wolf',
  'Zanussi',
  'everydrop',
  'everydrop by Whirlpool',

  // ── Specialty, compact, wine, beverage, outdoor, commercial ─────────────
  'Alpicool',
  'Antarctic Star',
  'AstroAI',
  'Avanti',
  'Avanti Products',
  'BEVOI',
  'Big Chill',
  'Black+Decker',
  'Blaze',
  'BODEGA',
  'BougeRV',
  'Bull Outdoor Products',
  'CLF',
  'CNCEST',
  "COMFEE'",
  'COOLSKI',
  'COSTWAY',
  'Coca-Cola',
  'CROWNFUL',
  'Danby',
  'Dometic',
  'DUURA',
  'EarthSmart',
  'ECEVT',
  'EdgeStar',
  'Elmira',
  'ERP',
  'EuroCave',
  'ExploreHorizon',
  'F2C Global Limited',
  'FoMup',
  'FreezLabs',
  'FrigiLife',
  'GAOMON',
  'GarveeHome',
  'GUNJI',
  'HCK',
  'hOmeLabs',
  'HOMCOM',
  'ICECO',
  'ICEJUNGLE',
  'Impecca USA',
  'Ivation',
  'Jeremy Cass LLC',
  'Jimmy US',
  'Kalamera',
  'Kegco',
  'KichKing',
  'Koolatron',
  'KoolMore',
  'KUCHT Professional',
  'LANBO',
  'LANBOPRO',
  'LifePlus',
  'Lynx',
  'Magic Chef',
  'MaksPRO',
  'Marvel',
  'MAXX Cold',
  'Maxx Ice',
  'MZAVP',
  "N'FINITY",
  'NewAir',
  'NIPUS',
  'NORBI',
  'Nostalgia',
  'NutriChef',
  'PeakCold',
  'Perlick',
  'PioneerWorks',
  'ProCool Creative Cooling Solutions',
  'SMAD',
  'SnowLion',
  'SPT',
  'Summit',
  'Summit Appliance',
  'SUNYOU',
  'ThermoStar',
  'THOR Kitchen',
  'True',
  'Turbo Air',
  'U-Line',
  'Unique Appliances',
  'VEVOR',
  'Vinotemp',
  'VIVOHOME',
  'WANAI',
  'Waterdrop UK',
  'Whynter',
  'WIE',
  'Wine Enthusiast',
  'XO Appliance',
  'YASHE',
  'YEEGO',
  'YUKOOL',
  'Zephyr',
  'ZLINE',
  'ZLINE Kitchen and Bath',
]

/**
 * Inputs that resolve to a canonical form OTHER than their own normalized
 * spelling: the owner's directed sub-brand renames, historic spellings, and
 * punctuation-free variants (slugs, feed exports).
 */
const BRAND_ALIASES: Readonly<Record<string, string>> = {
  // Owner-directed (FRIDGE-348): the GE sub-brands are sold under their own
  // names today.
  'ge cafe': 'Café',
  'ge café': 'Café',
  'ge monogram': 'Monogram',
  // Historic / punctuation variants of the same maker.
  'jenn air': 'JennAir',
  'black decker': 'Black+Decker',
  'black & decker': 'Black+Decker',
  'blackdecker': 'Black+Decker',
  'fisher paykel': 'Fisher & Paykel',
  'sub zero': 'Sub-Zero',
  'subzero': 'Sub-Zero',
  'u line': 'U-Line',
  'uline': 'U-Line',
  'coca cola': 'Coca-Cola',
  'comfee': "COMFEE'",
  'nfinity': "N'FINITY",
  'homelabs': 'hOmeLabs',
  'lg electronics inc': 'LG Electronics',
}

/**
 * Normalize a brand string into the lookup key: lower-case, accents
 * stripped, curly apostrophes straightened, hyphens/underscores/whitespace
 * collapsed to a single space, "and" folded to "&". Exported so callers that
 * build their own brand indexes can key them the same way.
 */
export function normalizeBrandKey(raw: string): string {
  return String(raw ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\s\-_]+/g, ' ')
    .replace(/\s+and\s+/g, ' & ')
    .replace(/\s*&\s*/g, ' & ')
    .replace(/[.,]+$/g, '')
    .trim()
}

const CASING_BY_KEY: ReadonlyMap<string, string> = (() => {
  const map = new Map<string, string>()
  for (const canonical of CANONICAL_BRANDS) map.set(normalizeBrandKey(canonical), canonical)
  for (const [alias, canonical] of Object.entries(BRAND_ALIASES)) {
    map.set(normalizeBrandKey(alias), canonical)
  }
  return map
})()

/** Every canonical display form this module knows, for tests and audits. */
export const KNOWN_BRAND_CASINGS: readonly string[] = CANONICAL_BRANDS

/**
 * The canonical display casing for a brand value, or the value untouched
 * when the brand is not one we know. null/undefined → ''.
 *
 *   displayBrandName('Lg Studio')   → 'LG Studio'
 *   displayBrandName('LG STUDIO')   → 'LG Studio'
 *   displayBrandName('KITCHENAID')  → 'KitchenAid'
 *   displayBrandName('Wrought Studio') → 'Wrought Studio'   (unknown: as-is)
 *   displayBrandName('advwin')      → 'advwin'              (unknown: as-is)
 */
export function displayBrandName(raw: string | null | undefined): string {
  if (typeof raw !== 'string') return ''
  const trimmed = raw.trim()
  if (!trimmed) return ''
  return CASING_BY_KEY.get(normalizeBrandKey(trimmed)) ?? raw
}

/** True when `raw` resolves to a canonical casing this module knows. */
export function isKnownBrand(raw: string | null | undefined): boolean {
  if (typeof raw !== 'string' || !raw.trim()) return false
  return CASING_BY_KEY.has(normalizeBrandKey(raw))
}

/**
 * Restore brand casing inside a spec VALUE that may be a single brand or a
 * delimited list of brands ("LG", "GE, GE Profile, Cafe", "Samsung / LG").
 * Segments that are not a known brand — and every separator, exactly as
 * written — pass through unchanged, so a value that names no brand at all is
 * returned byte-identical. This is what keeps `titleCaseValue("LG")` from
 * printing "Lg" while leaving "White" alone.
 */
export function restoreBrandCasing(value: string): string {
  if (typeof value !== 'string' || !value) return value
  const parts = value.split(/(\s*[,/;|]\s*)/)
  let changed = false
  const out = parts.map((part, i) => {
    if (i % 2 === 1) return part // separator (kept verbatim)
    if (!part.trim()) return part
    const cased = displayBrandName(part)
    if (cased !== part) changed = true
    return cased
  })
  return changed ? out.join('') : value
}
